import dayjs from 'dayjs';
import { BtnCopy } from '@components/buttons';
import type { CheckinSummary } from 'server/dbx/checkin-summaries';
import { CheckinFeedback } from 'shared/checkin-feedback';
import { isSprintWeek, summaryWeekNumber, SummarySchedule } from 'shared/checkin-weeks';
import { useIntl } from 'shared/intl/use-intl';
import { CoachFeedbackSection } from './checkin-history';
import { CheckinItemBody } from './checkin-item-body';
import { useSummaryTitle } from './weekly-summary-card';

type Props = {
  summaries: CheckinSummary[];
  schedule: SummarySchedule;
  feedback?: Record<UUID, CheckinFeedback>;
};

function weekOf(summary: CheckinSummary, schedule: SummarySchedule) {
  return {
    weekNumber: summaryWeekNumber(summary.weekStart, schedule.startDate) || 0,
    isSprint: isSprintWeek(summary.weekStart, schedule),
  };
}

/**
 * Every weekly summary, oldest first, for the Sprint Summary conversation,
 * which asks the student to paste them all in. The Sprint summary itself is
 * left out: it is what that conversation produces.
 */
export function weeklySummariesText(summaries: CheckinSummary[], schedule: SummarySchedule) {
  return summaries
    .map((s) => ({ s, week: weekOf(s, schedule) }))
    .filter(({ week }) => !week.isSprint)
    .sort((a, b) => (a.s.weekStart < b.s.weekStart ? -1 : 1))
    .map(({ s, week }) => `Week ${week.weekNumber}\n\n${s.body.trim()}`)
    .join('\n\n---\n\n');
}

export function SummaryHistory({ summaries, schedule, feedback }: Props) {
  const intl = useIntl();
  const titleFor = useSummaryTitle();
  if (!summaries.length) {
    return null;
  }
  const hasWeekly = summaries.some((s) => !weekOf(s, schedule).isSprint);

  return (
    <section class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">
          {intl('Your weekly summaries')}
        </h2>
        {hasWeekly && (
          <BtnCopy
            class="border border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
            margin="m-0"
            padding="px-3 py-1.5"
            value={weeklySummariesText(summaries, schedule)}
          >
            {intl('Copy all weekly summaries')}
          </BtnCopy>
        )}
      </div>
      {summaries.map((summary) => (
        <div
          key={summary.id}
          class="border border-gray-200 dark:border-gray-700 rounded-sm bg-white dark:bg-gray-800 p-4 flex flex-col gap-3"
        >
          <header class="flex flex-col">
            <strong class="text-gray-800 dark:text-gray-200">
              {titleFor(weekOf(summary, schedule))}
            </strong>
            {summary.submittedAt && (
              <span class="text-sm text-gray-500">
                {intl('Sent {when:string}', {
                  when: dayjs(summary.submittedAt).format('ddd, MMM D, h:mm A'),
                })}
              </span>
            )}
          </header>
          <CheckinItemBody kind="summary" answers={[]} body={summary.body} />
          {feedback?.[summary.id] && <CoachFeedbackSection feedback={feedback[summary.id]} />}
        </div>
      ))}
    </section>
  );
}
